import React, { FC, useEffect, useState } from 'react'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { NamePath, RuleObject } from 'rc-field-form/lib/interface'

import { Button, Form, FormLabelWithHint, Input, Modal, PasswordRulesHint } from '../../../common'
import hintStyles from '../../../common/form-label-with-hint/form-label-with-hint.module.scss'
import { openPasswordRecoveryModal } from '../../../../utils/auth-modal'
import { validateMessages } from '../../../../utils/constants'
import ModalUserProfile from '../common/modal/modal-user-profile'
import useChangePasswordManagerUserProfile from './hooks/use-change-password-manager-user-profile'
import { Close, Eye, EyeInvisible } from '../../../common/icons'
import formsModalStyles from '../../../forms/forms-modal/forms-modal.module.scss'
import styles from './change-password-user-profile.module.scss'
import {
  isPasswordValid,
  latinPasswordValidator,
  MIN_LEN_PASSWORD,
  oneDigitCharValidator,
  oneLatinLowerCharValidator,
  oneLatinUpperCharValidator,
  oneSpecialCharValidator,
  weakPasswordPatternsValidator,
} from '../../../../utils/ant-form-validators'

const { Item, useForm } = Form

type PropsChangePasswordUserProfile = {
  visibleForm: boolean
  onCloseForm: () => void
  description?: React.ReactNode
  isRequiredPasswordChange?: boolean
  onSuccess?: () => void
}

type GetFieldValue = {
  getFieldValue: (name: NamePath) => string
}

const ChangePasswordUserProfile: FC<PropsChangePasswordUserProfile> = (props) => {
  const [form] = useForm()
  const { lg: isDesktop } = useBreakpoint()
  const { visibleForm, onCloseForm, description, isRequiredPasswordChange = false, onSuccess } = props
  const { loading, methods } = useChangePasswordManagerUserProfile(
    form,
    isRequiredPasswordChange ? () => undefined : onCloseForm,
    {
      showSuccess: !isRequiredPasswordChange,
      onSuccess,
    },
  )

  const [disabledSave, setDisabledSave] = useState<boolean>(true)
  const [newPasswordHintActivated, setNewPasswordHintActivated] = useState(false)
  const [newPasswordFocused, setNewPasswordFocused] = useState(false)
  const [newPasswordValue, setNewPasswordValue] = useState('')
  const newPasswordHintVisible = newPasswordHintActivated && (newPasswordFocused || !isPasswordValid(newPasswordValue))
  const handleChange = () => {
    if (form.isFieldsTouched()) setDisabledSave(false)
  }

  const handleRecovery = () => {
    openPasswordRecoveryModal()
  }

  useEffect(() => {
    return () => {
      setDisabledSave(true)
      setNewPasswordHintActivated(false)
      setNewPasswordFocused(false)
      setNewPasswordValue('')
    }
  }, [visibleForm])

  const formContent = (
    <>
      {description && <p>{description}</p>}
      <Form
        form={form}
        onFieldsChange={handleChange}
        name="form-change-password"
        className={isRequiredPasswordChange ? styles.requiredPasswordForm : undefined}
        layout="vertical"
        validateMessages={validateMessages}
        autoComplete="off"
      >
        <Item
          name="currentPassword"
          label="Текущий пароль"
          rules={[
            {
              required: true,
              whitespace: true,
            },
          ]}
        >
          <Input.Password
            type="password"
            autoComplete="new-password"
            placeholder="Введите текущий пароль"
            iconRender={(visible) => (visible ? <Eye color="#818C99" /> : <EyeInvisible color="#818C99" />)}
          />
        </Item>
        {isRequiredPasswordChange && (
          <button type="button" className={styles.recovery} onClick={handleRecovery}>
            Восстановить пароль
          </button>
        )}
        <Item
          name="newPassword"
          label={
            <FormLabelWithHint
              label="Новый пароль"
              hint={<PasswordRulesHint />}
              overlayClassName={hintStyles.passwordRulesTooltip}
            />
          }
          messageVariables={{ label: 'Новый пароль' }}
          help={null}
          extra={
            newPasswordHintActivated ? (
              <PasswordRulesHint value={newPasswordValue} fieldHint visible={newPasswordHintVisible} />
            ) : null
          }
          className="password-rules-field"
          rules={[
            {
              required: true,
              whitespace: true,
              min: MIN_LEN_PASSWORD,
            },
            {
              validator: oneLatinLowerCharValidator,
            },
            {
              validator: oneLatinUpperCharValidator,
            },
            {
              validator: oneDigitCharValidator,
            },
            {
              validator: oneSpecialCharValidator,
            },
            {
              validator: latinPasswordValidator,
            },
            {
              validator: weakPasswordPatternsValidator,
            },
          ]}
        >
          <Input.Password
            type="password"
            autoComplete="new-password"
            placeholder="Введите новый пароль"
            onFocus={() => {
              setNewPasswordHintActivated(true)
              setNewPasswordFocused(true)
            }}
            onBlur={() => setNewPasswordFocused(false)}
            onChange={(event) => setNewPasswordValue(event.target.value)}
            iconRender={(visible) => (visible ? <Eye color="#818C99" /> : <EyeInvisible color="#818C99" />)}
          />
        </Item>
        <Item
          name="confirm"
          label="Подтверждение пароля"
          dependencies={['newPassword']}
          rules={[
            {
              required: true,
              whitespace: true,
            },
            ({ getFieldValue }: GetFieldValue) => ({
              validator: (rule: RuleObject, value: string) => {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve()
                }
                return Promise.reject('Два введенных вами пароля не совпадают!')
              },
            }),
          ]}
        >
          <Input.Password
            type="password"
            autoComplete="new-password"
            placeholder="Подтверждение пароля"
            iconRender={(visible) => (visible ? <Eye color="#818C99" /> : <EyeInvisible color="#818C99" />)}
          />
        </Item>
      </Form>
    </>
  )

  if (isRequiredPasswordChange) {
    return (
      <Modal
        visible={visibleForm}
        footer={null}
        onCancel={onCloseForm}
        className={formsModalStyles.modal}
        keyboard={false}
        destroyOnClose={true}
        closable={isDesktop}
        closeIcon={<Close />}
        width={'inherit'}
        zIndex={1100}
      >
        <div
          className={[formsModalStyles.modalBody, formsModalStyles.loginModalBody, styles.requiredPasswordBody].join(
            ' ',
          )}
        >
          <div className={formsModalStyles.swipeHolder} />
          <h1 className={formsModalStyles.modalH1}>Смена пароля</h1>
          {formContent}
          <div className={styles.requiredPasswordFooter}>
            <Button type="primary" onClick={methods.onSave} loading={loading} disabled={disabledSave}>
              Сохранить
            </Button>
            <Button onClick={onCloseForm}>Отменить</Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <ModalUserProfile
      title="Редактировать пароль"
      visible={visibleForm}
      onCancel={methods.onCancel}
      onSave={methods.onSave}
      loading={loading}
      disabledSave={disabledSave}
    >
      {formContent}
    </ModalUserProfile>
  )
}

export default ChangePasswordUserProfile
